import { CardModal, CardModalData, FlipCard, FlipCardData } from './interactivities';
import { ContentType } from '../models';

export enum CardSize {
  XS = 'xs',
  S = 's',
  M = 'm',
  L = 'l',
  XL = 'xl',
}

export enum CardByLine {
  XS = 7,
  S = 5,
  M = 3,
  L = 2,
  XL = 1,
}

export enum CardDirection {
  REGULAR = 'regular',
  REVERSE = 'reverse',
  ONLY_ICON = 'only-icon',
  ONLY_TEXT = 'only-text',
}

export enum CardIconType {
  ICON = 'icon',
  IMAGE = 'image',
}

export const cardByLineBound: { [key in CardSize]: number } = {
  [CardSize.XS]: 7,
  [CardSize.S]: 5,
  [CardSize.M]: 3,
  [CardSize.L]: 2,
  [CardSize.XL]: 1,
};

export function convertModalToFlip(modal: CardModalData, flip: FlipCardData): FlipCardData {
  const flatModalCards = modal.cardRows.flat().filter(card => card && card.slug);
  const flipCards = flatModalCards.map(modalCard => {
    const flipCard = new FlipCard({ slug: modalCard.slug });

    switch (flip.cardDirection) {
      case CardDirection.ONLY_ICON:
        if (modalCard.icon) {
          flipCard.firstIcon = modalCard.icon;
          flipCard.firstIconType = modalCard.iconType;
        }
        if (modalCard.title) {
          flipCard.secondHeader = modalCard.title;
        }
        break;

      case CardDirection.ONLY_TEXT:
        if (modalCard.title) {
          flipCard.firstHeader = modalCard.title;
        }
        if (modalCard.contents?.length) {
          const content = modalCard.contents.find(c => (c.title && c.title.trim()) || (c.content && c.content.trim()));
          if (content) {
            flipCard.secondHeader = content.title?.trim() ? content.title : content.content;
          }
        }
        break;

      case CardDirection.REVERSE:
      case CardDirection.REGULAR:
      default:
        if (modalCard.icon) {
          flipCard.firstIcon = modalCard.icon;
          flipCard.firstIconType = modalCard.iconType;
          flipCard.secondIcon = modalCard.icon;
          flipCard.secondIconType = modalCard.iconType;
        }
        if (modalCard.title) {
          flipCard.firstHeader = modalCard.title;
        }
        if (modalCard.contents?.length) {
          const content = modalCard.contents.find(c => (c.title && c.title.trim()) || (c.content && c.content.trim()));
          if (content) {
            flipCard.secondHeader = content.content;
          }
        }
        break;
    }
    return flipCard;
  });

  const cardsPerRow = cardByLineBound[flip.cardSize];
  const flipRows: FlipCard[][] = [];
  for (let i = 0; i < flipCards.length; i += cardsPerRow) {
    flipRows.push(flipCards.slice(i, i + cardsPerRow));
  }

  flip.cardRows = flipRows;
  flip.validationCriteria = modal.validationCriteria;
  flip.title = modal.title || '';

  return flip;
}

export function convertFlipToModal(flip: FlipCardData, modal: CardModalData): CardModalData {
  const flatFlipCards = flip.cardRows.flat().filter(card => card && card.slug);

  const modalCards = flatFlipCards.map(flipCard => {
    const modalCard: CardModal = {
      slug: flipCard.slug,
      icon: '',
      iconType: null,
      title: '',
      contents: [],
    };

    switch (flip.cardDirection) {
      case CardDirection.ONLY_ICON:
        if (flipCard.firstIcon) {
          modalCard.icon = flipCard.firstIcon;
          modalCard.iconType = flipCard.firstIconType;
        }
        if (flipCard.firstHeader) {
          modalCard.contents.push({
            type: ContentType.TEXT,
            content: flipCard.firstHeader,
            title: '',
          });
        }
        break;

      case CardDirection.ONLY_TEXT:
        if (flipCard.firstHeader) {
          modalCard.title = flipCard.firstHeader;
        }
        if (flipCard.secondHeader) {
          modalCard.contents.push({
            type: ContentType.TEXT,
            content: flipCard.secondHeader,
            title: '',
          });
        }
        break;

      case CardDirection.REVERSE:
      case CardDirection.REGULAR:
      default:
        if (flipCard.firstIcon) {
          modalCard.icon = flipCard.firstIcon;
          modalCard.iconType = flipCard.firstIconType;
        }
        if (flipCard.firstHeader) {
          modalCard.title = flipCard.firstHeader;
        }
        if (flipCard.secondHeader) {
          modalCard.contents.push({
            type: ContentType.TEXT,
            content: flipCard.secondHeader,
            title: '',
          });
        }
        break;
    }
    return modalCard;
  });
  const cardsPerRow = cardByLineBound[modal.cardSize];
  const modalRows: CardModal[][] = [];
  for (let i = 0; i < modalCards.length; i += cardsPerRow) {
    modalRows.push(modalCards.slice(i, i + cardsPerRow));
  }

  modal.cardRows = modalRows;
  modal.validationCriteria = flip.validationCriteria;
  modal.title = flip.title || '';

  return modal;
}

export function convertFlipToFlip(sourceFlip: FlipCardData, targetFlip: FlipCardData): FlipCardData {
  const flatSourceCards: FlipCard[] = sourceFlip.cardRows.flat().filter(card => card && card.slug);

  const newFlipCards: FlipCard[] = flatSourceCards.map(sourceCard => {
    const newCard = new FlipCard({ slug: sourceCard.slug });

    switch (targetFlip.cardDirection) {
      case CardDirection.ONLY_ICON:
        if (sourceCard.firstIcon) {
          newCard.firstIcon = sourceCard.firstIcon;
          newCard.firstIconType = sourceCard.firstIconType;
        }
        if (sourceCard.firstHeader) {
          newCard.secondHeader = sourceCard.firstHeader;
        }
        break;

      case CardDirection.ONLY_TEXT:
        if (sourceCard.firstHeader) {
          newCard.firstHeader = sourceCard.firstHeader;
        }
        if (sourceCard.secondHeader) {
          newCard.secondHeader = sourceCard.secondHeader;
        }
        break;

      case CardDirection.REGULAR:
      case CardDirection.REVERSE:
      default:
        if (sourceCard.firstIcon) {
          newCard.firstIcon = sourceCard.firstIcon;
          newCard.firstIconType = sourceCard.firstIconType;
          newCard.secondIcon = sourceCard.firstIcon;
          newCard.secondIconType = sourceCard.firstIconType;
        }
        if (sourceCard.firstHeader) {
          newCard.firstHeader = sourceCard.firstHeader;
        }
        if (sourceCard.secondHeader) {
          newCard.secondHeader = sourceCard.secondHeader;
        }
        break;
    }
    return newCard;
  });

  const cardsPerRow = cardByLineBound[targetFlip.cardSize];
  const newFlipRows: FlipCard[][] = [];
  for (let i = 0; i < newFlipCards.length; i += cardsPerRow) {
    newFlipRows.push(newFlipCards.slice(i, i + cardsPerRow));
  }
  if (newFlipRows.length === 0) {
    newFlipRows.push([]);
  }

  targetFlip.cardRows = newFlipRows;
  targetFlip.validationCriteria = sourceFlip.validationCriteria;
  targetFlip.title = sourceFlip.title || '';

  return targetFlip;
}

export function convertModalToModal(sourceModal: CardModalData, targetModal: CardModalData): CardModalData {
  const flatSourceCards = sourceModal.cardRows.flat().filter(card => card && card.slug);

  const newModalCards = flatSourceCards.map(sourceCard => {
    const newCard: CardModal = {
      slug: sourceCard.slug,
      icon: '',
      iconType: null,
      title: '',
      contents: [],
    };

    switch (targetModal.cardDirection) {
      case CardDirection.ONLY_ICON:
        if (sourceCard.icon) {
          newCard.icon = sourceCard.icon;
          newCard.iconType = sourceCard.iconType;
        }
        if (sourceCard.contents) {
          newCard.contents = sourceCard.contents.map(c => ({ ...c }));
        }
        break;

      case CardDirection.ONLY_TEXT:
        if (sourceCard.title) {
          newCard.title = sourceCard.title;
        }
        if (sourceCard.contents && sourceCard.contents.length > 0) {
          newCard.contents = sourceCard.contents.map(c => ({ ...c }));
        }
        break;

      case CardDirection.REGULAR:
      case CardDirection.REVERSE:
      default:
        if (sourceCard.icon) {
          newCard.icon = sourceCard.icon;
          newCard.iconType = sourceCard.iconType;
        }
        if (sourceCard.title) {
          newCard.title = sourceCard.title;
        }
        if (sourceCard.contents && sourceCard.contents.length > 0) {
          newCard.contents = sourceCard.contents.map(c => ({ ...c }));
        }
        break;
    }
    return newCard;
  });

  const cardsPerRow = cardByLineBound[targetModal.cardSize];
  const newModalRows: CardModal[][] = [];
  for (let i = 0; i < newModalCards.length; i += cardsPerRow) {
    newModalRows.push(newModalCards.slice(i, i + cardsPerRow));
  }

  if (newModalRows.length === 0) {
    newModalRows.push([]);
  }

  targetModal.cardRows = newModalRows;
  targetModal.validationCriteria = sourceModal.validationCriteria;
  targetModal.title = sourceModal.title || '';

  return targetModal;
}
